import { Grid, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";
import { formatMileage } from "../../../utils/labels/formatDistance";
import { MileageSummary } from "../../../utils/mileage";
import InfoBlock from "../InfoBlock";
import { testIds } from "./MileageBlock.testIds";

const UNAVAILABLE = "—";

function formatMileageDisplay(
  distanceInMeters: number | null | undefined
): string {
  if (distanceInMeters === null || distanceInMeters === undefined) {
    return UNAVAILABLE;
  }
  return formatMileage(distanceInMeters);
}

type MileageFieldProps = {
  label: string;
  value: string;
  testId: string;
};

const MileageField = ({ label, value, testId }: MileageFieldProps) => (
  <TextField
    label={label}
    value={value}
    size="small"
    fullWidth
    InputProps={{ readOnly: true }}
    InputLabelProps={{ shrink: true }}
    inputProps={
      {
        "data-testid": testId,
      } as React.InputHTMLAttributes<HTMLInputElement>
    }
    sx={{
      "& .MuiInputBase-input": {
        cursor: "default",
      },
    }}
  />
);

export type MileageBlockProps = {
  mileage: MileageSummary;
  namespace?: "trips" | "orders";
};

const MileageBlock = ({ mileage, namespace = "trips" }: MileageBlockProps) => {
  const { t } = useTranslation(namespace);

  return (
    <InfoBlock
      title={t("mileage", "Mileage")}
      dataTestId={
        namespace === "trips"
          ? testIds.mileageBlockTrips
          : testIds.mileageBlockOrders
      }
    >
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <MileageField
            label={t("routeMiles", "Route Miles")}
            value={formatMileageDisplay(mileage.routeMiles)}
            testId={testIds.mileageBlockRouteMilesInput}
          />
        </Grid>
        <Grid item xs={6}>
          <MileageField
            label={t("loadedMiles", "Loaded Miles")}
            value={formatMileageDisplay(mileage.loadedMiles)}
            testId={testIds.mileageBlockLoadedMilesInput}
          />
        </Grid>
        <Grid item xs={6}>
          <MileageField
            label={t("emptyMiles", "Empty Miles")}
            value={formatMileageDisplay(mileage.emptyMiles)}
            testId={testIds.mileageBlockEmptyMilesInput}
          />
        </Grid>
        <Grid item xs={12}>
          <MileageField
            label={t("actualTripMiles", "Actual (Trip) Miles")}
            value={formatMileageDisplay(mileage.actualMiles)}
            testId={testIds.mileageBlockActualTripMilesInput}
          />
        </Grid>
      </Grid>
    </InfoBlock>
  );
};

export default MileageBlock;
